import {
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
} from 'constants/uiConstants';
import { Tooltip } from 'antd';
import React from 'react';
import { IoIosArrowDropdownCircle } from 'react-icons/io';
import {
  PiArrowDown,
  PiArrowUp,
  PiChartPie,
  PiCurrencyDollar,
  PiLeaf,
  PiScales,
} from 'react-icons/pi';
import { RightPanelTabId } from '../../redux/slices/InsightsSlice';

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={13} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={13} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={13} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={13} />,
};

interface MergeSectionProps {
  tabId: RightPanelTabId;
  isExpanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  children: React.ReactNode;
}

const MergeSection: React.FC<MergeSectionProps> = ({
  tabId,
  isExpanded,
  isFirst,
  isLast,
  onToggle,
  onMoveUp,
  onMoveDown,
  children,
}) => {
  return (
    <div className="border-b last:border-b-0">
      {/* Section header */}
      <div className="group relative flex items-center border-b bg-gray-50 px-3 py-1.5">
        {/* Title — flex-1, clickable to toggle, truncates when narrow */}
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 cursor-pointer select-none items-center gap-1.5 overflow-hidden text-left"
        >
          {TAB_ICONS[tabId]}
          <span className="truncate text-xs font-semibold uppercase tracking-wider text-gray-dark">
            {RIGHT_PANEL_TAB_LABELS[tabId]}
            {tabId === RIGHT_PANEL_TAB_FINANCIAL && (
              <span className="ml-1 font-semibold normal-case tracking-normal text-secondary-dark">
                (Plan data only)
              </span>
            )}
          </span>
        </button>

        {/* Reorder arrows — absolute, sits left of expand icon.
            bg-gray-50 lets it visually cover the title when the panel is narrow. */}
        <div className="absolute inset-y-0 right-8 flex items-center gap-0.5 bg-gray-50 pl-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Tooltip title={<>Move up<br /><span className="opacity-75">Reorder this section higher</span></>} placement="left">
            <button
              type="button"
              aria-label="Move section up"
              disabled={isFirst}
              onClick={onMoveUp}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-muted hover:bg-gray-200 hover:text-gray-dark disabled:pointer-events-none disabled:opacity-30"
            >
              <PiArrowUp size={12} />
            </button>
          </Tooltip>
          <Tooltip title={<>Move down<br /><span className="opacity-75">Reorder this section lower</span></>} placement="left">
            <button
              type="button"
              aria-label="Move section down"
              disabled={isLast}
              onClick={onMoveDown}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-muted hover:bg-gray-200 hover:text-gray-dark disabled:pointer-events-none disabled:opacity-30"
            >
              <PiArrowDown size={12} />
            </button>
          </Tooltip>
        </div>

        {/* Expand/collapse icon — always at far right */}
        <button
          type="button"
          onClick={onToggle}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
          className="ml-1 flex-shrink-0 text-gray-dark"
        >
          <IoIosArrowDropdownCircle
            className={`transition-transform duration-200 ${
              isExpanded ? 'rotate-180' : ''
            }`}
            size={18}
          />
        </button>
      </div>

      {/* Collapsible content */}
      <div
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isExpanded ? 'max-h-[9999rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default MergeSection;
